import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  inputVariants,
} from "@elaniintc/goes-design-system";
import SearchInput from "../common/search-input";
import { useEffect, useState } from "react";
import { cn } from "@/utils/theme";
import { useDebounceValue } from "usehooks-ts";

interface SearchDropdownProps<T> {
  placeholder?: string;
  searchPlaceholder?: string;
  widthClassName?: string;
  contentWidthClassName?: string;
  data: Array<T>;
  isLoading: boolean;
  isFetching: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isEmpty: boolean;
  onSearch?: (query: string) => void;
  renderItem: (
    item: T,
    index: number,
    closeMenu: () => void,
    isSelectedShowing?: boolean,
  ) => React.ReactNode;
  selectedItem?: T;
  group?: "left";
}

function SearchDropdown<T>({
  placeholder = "Seleccionar",
  searchPlaceholder = "Buscar...",
  widthClassName,
  contentWidthClassName,
  group,
  data,
  isLoading,
  isFetching,
  hasNextPage,
  fetchNextPage,
  isEmpty,
  selectedItem,
  onSearch,
  renderItem,
}: SearchDropdownProps<T>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [deferredQuery] = useDebounceValue(searchQuery || "", 500);
  const [open, setOpen] = useState(false);
  const closeMenu = () => {
    setOpen(false);
    setSearchQuery("");
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchQuery("");
    }
  };

  // Debounce search
  useEffect(() => {
    if (!onSearch) return;
    onSearch(deferredQuery);
  }, [deferredQuery, onSearch]);

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="quiet"
          group={group}
          className={cn(
            "flex items-center group gap-2 !font-body",
            selectedItem ? "justify-start" : "justify-between",
            inputVariants({
              className: cn(
                "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
                widthClassName,
              ),
              group,
            }),
          )}
        >
          {selectedItem
            ? renderItem(selectedItem, -1, closeMenu, true)
            : placeholder}
          <Icon
            type="far"
            name="angle-down"
            className="group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body"
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={cn(
          "size-horizontal-xs max-w-[var(--size-horizontal-xs)] rounded-lg border-neutral-silent text-body-loud font-semibold bg-neutral-object",
          contentWidthClassName,
        )}
        side="bottom"
        align="start"
      >
        <div className="p-3 border-b border-neutral-silent -mx-1 h-[60px]">
          <SearchInput
            className="w-full font-normal"
            variant="md"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="max-h-[220px] overflow-auto pt-2 -mx-1">
          <Content<T>
            activeSearch={!!deferredQuery}
            isLoading={isLoading}
            data={data}
            renderItem={(item, idx) => renderItem(item, idx, closeMenu)}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            fetchNextPage={() => {
              fetchNextPage();
            }}
            isEmpty={isEmpty}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Content<T>({
  activeSearch,
  isLoading,
  data,
  isFetching,
  hasNextPage,
  fetchNextPage,
  isEmpty,
  renderItem,
}: {
  activeSearch: boolean;
  isLoading: boolean;
  data: Array<T>;
  isEmpty: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  renderItem: (
    item: T,
    index: number,
    closeMenu: () => void,
  ) => React.ReactNode;
  isFetching: boolean;
}) {
  if (isLoading || (activeSearch && isFetching)) {
    // loading the first page
    return (
      <div className="w-full h-[210px] flex items-center justify-center">
        <Icon
          type="far"
          name="spinner-third"
          className="text-on-disabled animate-spin text-2xl"
        />
      </div>
    );
  }

  if (hasNextPage && data.length === 0 && !activeSearch) {
    return (
      <div className="w-full h-[210px] flex items-center justify-center">
        <Button
          variant="ghost-blue"
          size="xs"
          type="button"
          disabled={isFetching}
          className="shadow-none"
          onClick={() => {
            if (isFetching) return;
            fetchNextPage();
          }}
        >
          Más resultados
          <Icon
            type="far"
            name={isFetching ? "spinner-third" : "plus"}
            className={cn("text-current", isFetching && "animate-spin")}
          />
        </Button>
      </div>
    );
  }

  if (isEmpty) {
    if (activeSearch) {
      return (
        <div className="w-full h-[210px] flex items-center justify-center">
          <span className="text-on-disabled font-semibold">
            No se han encontrado resultados
          </span>
        </div>
      );
    }
    return (
      <div className="w-full h-[210px] flex items-center justify-center">
        <span className="text-on-disabled font-semibold">
          No hay más resultados
        </span>
      </div>
    );
  }

  return (
    <>
      <ul className="px-2 min-h-32">
        {data?.map((item, idx) => renderItem(item, idx, () => {}))}
      </ul>
      {!activeSearch && hasNextPage && (
        <div className="w-full border-t border-neutral-quiet p-3 flex items-center justify-start gap-3">
          <Button
            variant="ghost-blue"
            size="xs"
            type="button"
            disabled={isFetching}
            onClick={() => {
              if (isFetching) return;
              fetchNextPage();
            }}
          >
            Más resultados
            <Icon
              type="far"
              name={isFetching ? "spinner-third" : "plus"}
              className={cn("text-current", isFetching && "animate-spin")}
            />
          </Button>
        </div>
      )}
    </>
  );
}

export { SearchDropdown };
